import { yupResolver } from "@hookform/resolvers/yup";
import { createColumnHelper } from "@tanstack/react-table";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import downArrow from "src/assets/down-arrow.svg";
import { Button, Input, Modal } from "src/components";
import Loader from "src/components/ui/loader.component";
import VersioningCrate from "src/components/Versioning/versioningModal.component";
import { flattenObject, generateUniqueID } from "src/constants/functions";
import { invoiceActions } from "src/context/invoice-slice";
import { RootState } from "src/context/store";
import { useEventStatus } from "src/hooks/useEventStatus";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { toast } from "src/hooks/useToast";
import {
  entryInvoice,
  fetchInvoicesData,
  getInvoiceActionAllowed,
  getInvoiceBatchList,
  getInvoiceChangeValidationV1,
  getInvoiceDetails,
  getInvoicePriceList,
  getOrderedProducts,
  getProductGstDetails,
  getSimilarCompanyProducts,
} from "src/services/invoice-api";
import Book from "../../assets/book.png";
import DeleteIcon from "../../assets/delete.svg";
import editPen from "src/assets/edit-pen.svg";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import SubTable from "../counter-details/sub-table.component";
import { ewayData, getInvoiceDetailsInfo } from "../invoice-master/invoice-helper";
import InvoiceLayout from "../invoice-master/invoice-layout.component";
import InvoiceDetailCard from "./invoice-detail.component";
import { Transition } from "@headlessui/react";
import { MessageCircle } from "lucide-react";
import InvoiceRemarks from "./InvoiceRemarks";
import InvoiceEntryForm from "./InvoiceEntryCards/InvoiceEntryForm";
import { priceIdentifierList } from "./invoice-constants";
import { schema, schema2 } from "./invoice-constants";
import EwayDetails from "./InvoiceEntryCards/EwayDetails";
import { generateShortUniqueKey, validateInvoiceProducts } from "./InvoiceEntryCards/InvoiceEntryUtility";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";
import useDebounceLeading from "src/hooks/useDebounceLeading";

const columnHelper = createColumnHelper();

interface selectedPriceIdentifier {
  key: string;
  description: string;
  priceldentifier_id: number;
}

export const InvoiceEntryScreen = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [cashDiscountPercent, setCashDiscountPercent] = useState(0);
  const [cashDiscountAmount, setCashDiscountAmount] = useState(0);
  const { invoiceDetails,
    invoicesTextractData,
    invoiceData,
    currentInvoiceIndex,
    InvoiceFilters,
    currentPageIndex } = useSelector((state: RootState) => state.invoice);
  const [selectedPriceIdentifier, setSelectedPriceIdentifier] = useState<selectedPriceIdentifier>({
    key: "",
    description: "",
    priceldentifier_id: 0,
  });
  const [quantity, setQuantity] = useState<number>(0);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [useOrderedProducts, setUseOrderedProducts] = useState("ordered");
  const [itemCode, setItemCode] = useState<string>("");
  const [priceList, setPriceList] = useState<any[]>([]);
  const [batchList, setBatchList] = useState<any[]>([]);
  const [gstAmbiguous, setGstAmbiguous] = useState<boolean>(false);
  const [isConfirmingInvoice, setConfirmingInvoice] = useState<boolean>(false);
  const [netInvoiceAmount, setNetInvoiceAmount] = useState<string>("");
  const [selectedBatch, setSelectedBatch] = useState<any>({});
  const [mappedData, setMappedData] = useState<any[]>([]);
  const [tcs, setTcs] = useState<number>(0);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [VersioningModal, setVersioningModal] = useState(false);
  const [unknownBatch, setUnknownBatch] = useState<any[]>([]);
  const outSideClickRef = useOutsideClick(() => {
    setVersioningModal(false);
  });
  const [isRemarksShown, setIsRemarksShown] = useState(false);
  const [editingTextractProduct, setEditingTextractProduct] = useState<any>({});
  const [editingProductId, setEditingProductId] = useState<any>(null);
  const [isEwayApplicable, setIsEwayApplicable] = useState(false);
  const [isEwayExempt, setEwayExempt] = useState(false)
  const ref = useOutsideClick(() => {
    setIsRemarksShown(false);
  });

  const { hasPermission: viewRemarks } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.module,
    action: fineGrainedPermissionsKeys.INVOICE.VIEWREMARKS.action,
  });

  const company_code = invoiceDetails?.company_code;

  const navigate = useNavigate();
//M10-9862
  const location = useLocation();
  const { CompanyCode } = location.state || {};

  const smartFormInstance = useForm({
    // @ts-ignore
    resolver: yupResolver(useOrderedProducts === "unknown" ? schema2 : schema),
    mode: "onBlur",
    defaultValues: structuredClone({}),
  });

  function getNextInvoice(invoiceData: any, currentInvoiceIndex: any) {
    // Ensure the next index is within bounds
    if (currentInvoiceIndex + 1 < invoiceData.length) {
      return invoiceData[currentInvoiceIndex + 1];
    } else {
      // No next invoice (end of the list)
      return null;
    }
  }

  const { withEventCheck } = useEventStatus();
  const dispatch = useDispatch();
  const { stockist_invoice_attachment_id } = useParams();


  const searchParams = useMemo(
    () => ({
      company_code: InvoiceFilters?.company?.map((item) => item.code)?.join(",") || "",
      division_code: InvoiceFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceFilters?.dateRange?.from,
      endDate: InvoiceFilters?.dateRange?.to,
      assignee_id: InvoiceFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      ordering: InvoiceFilters?.sorting?.[0]?.key || "DESC",
      fuid_status_id: InvoiceFilters?.status?.map((item) => item.fuid_status_id)?.join(",") || 1,
      page_size: 10,
      page_number: 1,
      screen: "Entry",
      dateType: InvoiceFilters?.dateType?.[0]?.key || "",
    }),
    [InvoiceFilters]
  );

  const fetchMoreDataAfterInvoiceEntry = async () => {
    if (isLoading) return;
    try {
      const nextPageParams = { ...searchParams, page_number: currentPageIndex + 1 };
      const res = await fetchInvoicesData(nextPageParams);
      const newData = res?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        return;
      }
      dispatch(invoiceActions.setInvoiceData([...(invoiceData || []), ...newData]));
      dispatch(invoiceActions.setCurrentPageIndex(currentPageIndex + 1));
    } catch (error) {
      console.error("Error fetching more invoices", error);
    } finally {
      setIsLoading(false);
    }
  };

  //write the function if next invoice is not available then call fetchMoreDataAfterInvoiceEntry
  useEffect(() => {
    // Trigger fetch when the user is within 2 items of the end
    if (invoiceData.length - currentInvoiceIndex <= 2) {
      fetchMoreDataAfterInvoiceEntry();
    }
  }, [currentInvoiceIndex, invoiceData.length]);

  const columns = [
    //M10-9670 Start
    columnHelper.accessor("sno", {
      header: "S.No",
      cell: (info: any) => info.row.index + 1,
    }),//M10-9670 End
    columnHelper.accessor("item_description", {
      header: "Item Name",
      cell: (info: any) => info.row.original.item_description || "-",
    }),
    columnHelper.accessor("item_code", {
      header: "Item Code",
      cell: (info: any) => info.row.original.item_code || "-",
    }),
    columnHelper.accessor("inv_mrp", {
      header: "Inv. MRP",
      cell: (info: any) => info.row.original.inv_mrp || "-",
    }),
    columnHelper.accessor("ptr", {
      header: "Inv. PTR",
      cell: (info: any) => info.row.original.ptr || "-",
    }),
    columnHelper.accessor("inv_rate", {
      header: "Inv. Rate",
      cell: (info: any) => info.row.original.inv_rate || "-",
    }),
    columnHelper.accessor("quantity", {
      header: "Qty.",
      cell: (info: any) => info.row.original.quantity,
    }),
    columnHelper.accessor("free_qty", {
      header: "Free Qty",
      cell: (info: any) => info.row.original.free_qty || "0",
    }),
    columnHelper.accessor("gross_amt", {
      header: "Gross Amt.",
      cell: (info: any) => info.row.original.gross_amt || "-",
    }),
    columnHelper.accessor("discount_type", {
      header: "Discount Defined By",
      cell: (info: any) => info.row.original.discount_type || "-",
    }),
    columnHelper.accessor("discount_amt", {
      header: "Discount %",
      cell: (info: any) => {
        const { discount_amt, inv_rate, quantity } = info.row.original;
        if (!quantity || quantity === 0 || !inv_rate) return "0.00";
        const discountPercent = (discount_amt / (inv_rate * quantity)) * 100;
        return discountPercent.toFixed(2);
      },
    }),

    columnHelper.accessor("gst", {
      header: "GST %",
      cell: (info: any) => info.row.original.gst || info.row.original.p_gst_perc || "0",
    }),
    columnHelper.accessor("batch_no", {
      header: "Batch No.",
      cell: (info: any) => info.row.original.batch_no || "-",
    }),
    columnHelper.accessor("action", {
      header: "Action",
      cell: (info: any) => {
        const handleDelete = async (uniqueKey: string) => {
          setMappedData((prev) => prev.filter((item: any) => item?.unique_key !== uniqueKey));
        };
        return (
          <Button onClick={() => handleDelete(info.row.original.unique_key)}>
            <img src={DeleteIcon} alt="delete" className="w-4 h-4" />
          </Button>
        );
      },
    }),
    columnHelper.accessor("Edit", {
      header: "Edit",
      cell: (info: any) => {
        const handleEdit = async (uniqueKey: string) => {
          const { setValue }: any = smartFormInstance;
          setEditingProductId(info.row.original.invoice_product_id);
          setValue("itemName", info.row.original.item_description);
          setValue("itemCode", info.row.original.item_code);
          // setValue("mrp", value?.mrp || "");
          // setValue("price", value?.ptr || "");
          setPrice(info.row.original.ptr);
          setValue("batchNo", info.row.original.batch_no);
          setValue("discountAmount", info.row.original.discount_amt);
          setValue("discountPercentage", Number(info.row.original.discount_perc));
          setValue("freeQty", Number(info.row.original.free_qty));
          setValue("quantity", info.row.original.quantity);
          setValue("schemeType", info.row.original.discount_type);
          // const res = await getInvoicePriceList(info.row.original.item_code);
          setValue("gst", Number(info.row.original.p_gst_perc) > 0 ? info.row.original.p_gst_perc : Number(info.row.original.gst) > 0 ? info.row.original.gst : "0");
          setValue("itemPrice", { mrp: info.row.original.inv_mrp, ptr: info.row.original.ptr });
          setValue("price", Number(info.row.original.ptr));
          setValue("mrp", Number(info.row.original.inv_mrp));
          setValue("ptr", info.row.original.ptr);
          setSelectedBatch(info.row.original.batch_no);
          setQuantity(info.row.original.quantity);
          setDiscountPercentage(Number(info.row.original.discount_perc));
          setDiscountAmount(Number(info.row.original.discount_amt));

          if (info.row.original.discount_type === "Scheme") {
            setSelectedPriceIdentifier({
              priceldentifier_id: 3,
              description: "Scheme",
              key: "SCHEME",
            });
            setValue("schemeType", {
              priceldentifier_id: 3,
              description: "Scheme",
              key: "SCHEME",
            });
          } else if (info.row.original.discount_type === "Discount Percent") {
            setSelectedPriceIdentifier({
              priceldentifier_id: 1,
              description: "Discount Percent",
              key: "DISCOUNT_PERCENT",
            });
            setValue("schemeType", {
              priceldentifier_id: 1,
              description: "Discount Percent",
              key: "DISCOUNT_PERCENT",
            });
          } else if (info.row.original.discount_type === "Discount Amount") {
            setSelectedPriceIdentifier({
              priceldentifier_id: 2,
              description: "Discount Amount",
              key: "DISCOUNT_AMOUNT",
            });
            setValue("schemeType", {
              priceldentifier_id: 2,
              description: "Discount Amount",
              key: "DISCOUNT_AMOUNT",
            });
          }

          setItemCode(info.row.original.item_code);
          setMappedData((prev) => prev.filter((item: any) => item?.unique_key !== uniqueKey));
        };
        return (
          <Button
            onClick={() => {
              setEditingTextractProduct(info.row.original);
              handleEdit(info.row.original.unique_key);
            }}
          >
            <img src={editPen} alt="delete" className="w-4 h-4" />
          </Button>
        );
      },
    }),
  ];
  const columns2 = [
    //M10-9670 Start
    columnHelper.accessor("sno", {
      header: "S.No",
      cell: (info: any) => info.row.index + 1,
    }),//M10-9670 End
    columnHelper.accessor("item_description", {
      header: "Item Name",
      cell: (info: any) => info.row.original.item_description || "-",
    }),
    columnHelper.accessor("item_code", {
      header: "Item Code",
      cell: (info: any) => info.row.original.item_code || "-",
    }),
    columnHelper.accessor("inv_mrp", {
      header: "Inv. MRP",
      cell: (info: any) => info.row.original.inv_mrp || "-",
    }),
    columnHelper.accessor("ptr", {
      header: "Inv. PTR",
      cell: (info: any) => info.row.original.ptr || "-",
    }),
    columnHelper.accessor("quantity", {
      header: "Qty.",
      cell: (info: any) => info.row.original.quantity,
    }),
    columnHelper.accessor("gross_amt", {
      header: "Gross Amt.",
      cell: (info: any) => info.row.original.gross_amt || "-",
    }),
    columnHelper.accessor("discount_type", {
      header: "Discount Defined By",
      cell: (info: any) => info.row.original.discount_type || "-",
    }),
    columnHelper.accessor("discount_amt", {
      header: "Discount %",
      cell: (info: any) => {
        const { discount_amt, inv_rate, quantity } = info.row.original;
        if (!quantity || quantity === 0 || !inv_rate) return "0.00";
        const discountPercent = (discount_amt / (inv_rate * quantity)) * 100;
        return discountPercent.toFixed(2);
      },
    }),

    columnHelper.accessor("free_qty", {
      header: "Free Qty",
      cell: (info: any) => info.row.original.free_qty || "0",
    }),

    columnHelper.accessor("gst", {
      header: "GST %",
      cell: (info: any) => info.row.original.gst || info.row.original.p_gst_perc || "0",
    }),
    columnHelper.accessor("batch_no", {
      header: "Batch No.",
      cell: (info: any) => info.row.original.batch_no || "-",
    }),
    columnHelper.accessor("action", {
      header: "Action",
      cell: (info: any) => {
        const handleDelete = async (uniqueKey: string) => {
          setMappedData((prev) => prev.filter((item: any) => item?.unique_key !== uniqueKey));
        };
        return (
          <Button onClick={() => handleDelete(info.row.original.unique_key)}>
            <img src={DeleteIcon} alt="delete" className="w-4 h-4" />
          </Button>
        );
      },
    }),
    columnHelper.accessor("Edit", {
      header: "Edit",
      cell: (info: any) => {
        const handleEdit = async (uniqueKey: string) => {
          const { setValue }: any = smartFormInstance;
          setEditingProductId(info.row.original.invoice_product_id);
          setValue("itemName", info.row.original.item_description);
          setValue("itemCode", info.row.original.item_code);
          // setValue("mrp", value?.mrp || "");
          // setValue("price", value?.ptr || "");
          setPrice(info.row.original.ptr);
          setValue("batchNo", info.row.original.batch_no);
          setValue("discountAmount", info.row.original.discount_amt);
          setValue("discountPercentage", Number(info.row.original.discount_perc));
          setValue("freeQty", Number(info.row.original.free_qty));
          setValue("quantity", info.row.original.quantity);
          setValue("schemeType", info.row.original.discount_type);
          // const res = await getInvoicePriceList(info.row.original.item_code);
          setValue("gst", Number(info.row.original.p_gst_perc) > 0 ? info.row.original.p_gst_perc : Number(info.row.original.gst) > 0 ? info.row.original.gst : "0");
          setValue("itemPrice", { mrp: info.row.original.inv_mrp, ptr: info.row.original.ptr });
          setValue("price", Number(info.row.original.ptr));
          setValue("mrp", Number(info.row.original.inv_mrp));
          setValue("ptr", info.row.original.ptr);
          setSelectedBatch(info.row.original.batch_no);
          setQuantity(info.row.original.quantity);
          setDiscountPercentage(Number(info.row.original.discount_perc));
          setDiscountAmount(Number(info.row.original.discount_amt));

          if (info.row.original.discount_type === "Scheme") {
            setSelectedPriceIdentifier({
              priceldentifier_id: 3,
              description: "Scheme",
              key: "SCHEME",
            });
            setValue("schemeType", {
              priceldentifier_id: 3,
              description: "Scheme",
              key: "SCHEME",
            });
          } else if (info.row.original.discount_type === "Discount Percent") {
            setSelectedPriceIdentifier({
              priceldentifier_id: 1,
              description: "Discount Percent",
              key: "DISCOUNT_PERCENT",
            });
            setValue("schemeType", {
              priceldentifier_id: 1,
              description: "Discount Percent",
              key: "DISCOUNT_PERCENT",
            });
          } else if (info.row.original.discount_type === "Discount Amount") {
            setSelectedPriceIdentifier({
              priceldentifier_id: 2,
              description: "Discount Amount",
              key: "DISCOUNT_AMOUNT",
            });
            setValue("schemeType", {
              priceldentifier_id: 2,
              description: "Discount Amount",
              key: "DISCOUNT_AMOUNT",
            });
          }

          setItemCode(info.row.original.item_code);
          setMappedData((prev) => prev.filter((item: any) => item?.unique_key !== uniqueKey));
        };
        return (
          <Button
            onClick={() => {
              setEditingTextractProduct(info.row.original);
              handleEdit(info.row.original.unique_key);
            }}
          >
            <img src={editPen} alt="delete" className="w-4 h-4" />
          </Button>
        );
      },
    }),
  ];

  const handleFormSubmit = async (data: any) => {
    if (Number(data?.freeQty) < 0 || !Number.isInteger(Number(data?.freeQty))) {
      toast({
        description: `Free Quantity can not be decimal or less than 0`,
        variant: "destructive",
      });
      return;
    }
    if (Number(quantity) < 0) {
      toast({
        description: `Quantity cannot be less than or equal to 0`,
        variant: "destructive",
      });
      return;
    }
    if (selectedPriceIdentifier?.key === "SCHEME") {
      if (Number(data?.freeQty) < 0) {
        toast({
          description: `Free Quantity cannot be less than 0`,
          variant: "destructive",
        });
        return;
      }
    }
    if (selectedPriceIdentifier?.key === "DISCOUNT_PERCENT" || selectedPriceIdentifier?.key === "DISCOUNT_AMOUNT") {
      if (Number(data?.discountPercentage) < 0 || Number(data?.discountPercentage) > 100) {
        toast({
          description: `Discount Percentage cannot be less than 0`,
          variant: "destructive",
        });
        return;
      }
    }
    const newBatchNo = data?.batchNo?.toUpperCase() as string;
    let newItemCode;

    if (useOrderedProducts === "unknown") {
      newItemCode = data?.itemCode !== undefined ? String(data?.itemCode) : generateShortUniqueKey()
    }

    try {
      const payload = {
        invoice_id: Number(invoiceDetails?.invoice?.invoice_id),
        item_description: useOrderedProducts === "unknown" ? String(data?.itemName) : String(data?.itemName),
        item_code: useOrderedProducts === "unknown" ? newItemCode : String(data?.itemCode),
        unique_key: editingProductId || String(data?.itemCode) + selectedBatch?.batch_no,
        quantity: Number(quantity) || 0,
        gst: data?.gst || "0",
        cash_dis_perc: cashDiscountPercent,
        ptr: Number(data?.ptr) || "0",
        discount_type: !selectedPriceIdentifier?.description ? "Discount Amount" : String(selectedPriceIdentifier?.description),
        free_qty: Number(data?.freeQty) || 0,
        batch_no: selectedBatch?.batch_no?.toUpperCase() || newBatchNo,
        inv_rate: String(price) || "0",
        inv_mrp: String(data?.mrp) || "0",
        discount_amt: !selectedPriceIdentifier?.description ? "0" : isNaN(Number(discountAmount)) ? "0" : String(discountAmount),
        counter_code: invoiceDetails?.counter_code,
        is_item_unknown: useOrderedProducts === "unknown",
        gross_amt: (Number(price) * Number(quantity))?.toFixed(2),
        ...(editingTextractProduct && {
          invoice_product_id: editingTextractProduct.invoice_product_id
        }),
        ucode: generateUniqueID()
      };
      setEditingTextractProduct([])
      setMappedData((prev) => [...prev, payload]);
      if (newBatchNo && !batchList.some((batch) => batch.batch_no === newBatchNo)) {
        setUnknownBatch((prev) => [...prev, { batch_no: newBatchNo }]);
      }
      resetFormFields();
      setSelectedBatch({});
      setEditingProductId(null);
      // setBatchNo("");
      // Focus the item name field after form submission
      const itemNameField = document.querySelector('input[name="itemName"]');
      if (itemNameField instanceof HTMLElement) {
        itemNameField?.focus();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCashDiscountPercentChange = (value: string) => {
    const percent = parseFloat(value) || 0;
    setCashDiscountPercent(Number(percent?.toFixed(2)));
    setMappedData((prev) => {
      const updatedData = prev.map((item) => ({ ...item, p_cash_discount_perc: Number(percent?.toFixed(2)) }));
      return updatedData;
    });
    const discountAmount = (calculateSubtotal() * percent) / 100;
    setCashDiscountAmount(Number(discountAmount?.toFixed(2)));

    updateTotal(discountAmount);
  };

  const handleCashDiscountAmountChange = (value: string) => {
    const amount = parseFloat(value) || 0;
    setCashDiscountAmount(Number(amount?.toFixed(2)));

    const percent = (amount / calculateSubtotal()) * 100;
    setCashDiscountPercent(Number(percent?.toFixed(2)));

    setMappedData((prev) => {
      const updatedData = prev.map((item) => ({ ...item, p_cash_discount_perc: Number(percent?.toFixed(2)) }));
      return updatedData;
    });

    updateTotal(amount);
  };

  const updateTotal = (discountAmount: number) => {
    return (calculateSubtotal() - discountAmount).toFixed(2);
  };

  const rowsForItemsAddedTable = (isUnknown: boolean) => {
    const rows: any[] = [];
    mappedData?.length > 0 &&
      mappedData
        ?.filter((item: any) => item?.is_item_unknown === isUnknown)
        .forEach((element: any) => {
          rows.push(flattenObject(element));
        });
    return rows;
  };

  const fetchInvoiceDetails = async () => {
    setIsLoading(true);
    try {
      const response = await getInvoiceDetails(stockist_invoice_attachment_id as string, false, CompanyCode as string);//M10-9862
      dispatch(invoiceActions.setInvoiceDetails(response?.data?.data));

      if (invoicesTextractData?.length > 0) {
        setMappedData(invoicesTextractData);
      } else if (response?.data?.data?.invoice?.textractProducts?.length > 0) {
        setMappedData(response?.data?.data?.invoice?.textractProducts || []);
        navigate(`/transactions/invoice/product-mapping/${stockist_invoice_attachment_id}`);
      } else {
        setMappedData([]);
      }
      if (invoicesTextractData?.length == 0) {
        dispatch(
          invoiceActions.setInvoicesTextractData(
            response?.data?.data?.invoice?.textractProducts?.map((item: any) => ({
              invoice_product_id: item?.invoice_product_id,
              unique_key: item?.invoice_product_id,
              invoice_id: response?.data?.data?.invoice?.invoice_id,
              item_description: item?.item_description,
              item_code: item?.item_code,
              quantity: item?.quantity,
              gst: item?.p_gst_perc,
              cash_dis_perc: item?.p_cash_discount_perc ?? 0,
              ptr: Number(item?.ptr),
              discount_type: item?.discount_type,
              free_qty: item?.free_qty,
              batch_no: item?.batch_no,
              inv_rate: String(item?.inv_rate),
              inv_mrp: String(item?.inv_mrp),
              discount_amt: String(item?.discount_amt),
              counter_code: response?.data?.data?.counter_code,
              is_item_unknown: item?.is_item_unknown,
              gross_amt: String((Number(item?.ptr) * Number(item?.quantity))?.toFixed(2)),
            })) || []
          )

        );
      }
      const isEwayExempt = response?.data?.data?.orderCodes?.every(
        (code: any) => code?.reference_order?.eway_exempt === true
      );
      setEwayExempt(isEwayExempt);

      if (response?.data?.data?.eWayBillDtl?.einvoice_status !== "accepted" && !isEwayExempt) {
        setIsEwayApplicable(response?.data?.data?.is_eway_applicable);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };
  const isRequestInProgress = useRef(false);

  const handleSubmitInvoice = useDebounceLeading(async () => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;
    const nextInvoice = getNextInvoice(invoiceData, currentInvoiceIndex);
    dispatch(invoiceActions.setCurrentInvoiceIndex(currentInvoiceIndex + 1));
    const invoiceActionAllowedResponse = await getInvoiceActionAllowed(invoiceDetails?.invoice?.invoice_id,
      invoiceDetails?.stockist_invoice_attachment_id, "Entry", invoiceDetails?.company_code);
    if (!invoiceActionAllowedResponse?.data?.data?.allowed) {
      toast({ description: invoiceActionAllowedResponse?.data?.data?.message, variant: "destructive" });
      return;
    }
    const calculatedTotal = calculateTotal();
    const difference = Math.abs(Number(calculatedTotal) - Number(netInvoiceAmount));
    const newArrayWithoutUniqueKey = mappedData?.map(({ unique_key, ...rest }) => rest);
    const isValid = validateInvoiceProducts(newArrayWithoutUniqueKey);
    if (!isValid) {
      return;
    }

    if (!netInvoiceAmount) {
      toast({
        description: `Please enter net amount`,
        variant: "destructive",
      });
      return;
    }

    if (difference > 10) {
      toast({
        description: `The difference between calculated amount and net amount should not exceed ₹10`,
        variant: "destructive",
      });
      return;
    }

    if (mappedData?.length === 0) {
      toast({
        description: `Please add at least one product`,
        variant: "destructive",
      });
      return;
    }
    setConfirmingInvoice(true);

    const currentDate = new Date(invoiceDetails?.invoice?.new_invoice_date);
    const currentYear = currentDate.getFullYear();
    const financialYearStart = new Date(currentDate.getMonth() >= 3 ? currentYear : currentYear - 1, 3, 1);

    const invoiceAccYear = financialYearStart.getFullYear();

    const payloadToValidateInV1 = {
      "cmpstockistcode": invoiceDetails?.company_stockist_code,
      "invoicenumber_searchcolumn": invoiceDetails?.invoice?.invoice_number || "",
      "invoice_accyear": invoiceAccYear,
      "company_code": invoiceDetails?.company_code
    }

    const response = await getInvoiceChangeValidationV1(payloadToValidateInV1)
    if (response?.data?.success !== "success") {
      toast({ description: response?.data?.message, variant: "destructive" });
      setIsLoading(false);
      return;
    }

    try {
      const payload2 = {
        data: {
          invoice_id: Number(invoiceDetails.invoice?.invoice_id),
          invoice_number: invoiceDetails?.invoice?.invoice_number,
          cash_discount_perc: String(cashDiscountPercent),
          cash_discount: String(cashDiscountAmount),
          net_amount: netInvoiceAmount,
          stockist_invoice_attachment_id: invoiceDetails?.stockist_invoice_attachment_id,
          // gst_perc: response?.data?.data?.invoice?.gst_perc || "",
          gst_amount: calculateTotalGst()?.toFixed(2),
          gross_amount: String(calculateSubtotal()),
          tcs: String(tcs) || 0,
          tcs_perc: Number(tcs) || 0,
          gst_perc: ((calculateTotalGst() / calculateSubtotal()) * 100).toFixed(2),
          tcs_amt: ((calculateSubtotal() - (calculateSubtotal() * cashDiscountPercent) / 100 + calculateTotalGst()) * tcs) / 100,
          confirm: true,
          stockist_id: invoiceDetails?.stockist_id,
          is_manual_discount: cashDiscountAmount > 0,
          // invoice header details sending for backend versioning
          u_code: invoiceDetails?.invoice?.u_code,
          new_invoice_date: invoiceDetails?.invoice?.new_invoice_date,
          // ...(qrIRN?.trim() !== "" && { e_inv_ref_type: qrIRN }),
          e_inv_ref_type: invoiceDetails?.invoice?.e_inv_ref_type || null,
          irn: invoiceDetails?.invoice?.irn,
          gst_validated: invoiceDetails?.invoice?.gst_validated,
          dl_validated: invoiceDetails?.invoice?.dl_validated,
          mci_validated: invoiceDetails?.invoice?.mci_validated,
          counter_gst_number: invoiceDetails?.counter_gst_number || "",
          counter_dl_number: invoiceDetails?.counter_dl_number || "",
          counter_mci_number: invoiceDetails?.counter_mci_number || "",
          invoice_num_modified: false,
          counter_data_validation: false,
          invoiceProducts: newArrayWithoutUniqueKey,
          textract: invoicesTextractData.length > 0 ? true : false,
        },
        primary_key: Number(invoiceDetails?.invoice?.invoice_id),
        event_type: "INVOICE_EDIT",
      };
      const res = await entryInvoice(payload2, company_code);
      toast({ description: "Invoice confirmed successfully", variant: "default" });

      try {
        await withEventCheck(
          () => {
            navigate(`/transactions/invoice/${nextInvoice?.stockist_invoice_attachment_id}/${0}`);
          },
          res?.data?.data?.[res?.data?.data]?.eventStreamCreated?.streamId
        );
        // dispatch(invoiceActions.setInvoiceDetails(response?.data?.data));
        resetFormFields();
        setIsLoading2(false);
        // navigate("/transactions/invoice");
      } catch (error: any) {
        console.log(error);
        toast({ description: error?.response?.data?.message, variant: "destructive" });
        setConfirmingInvoice(false);
        setIsLoading2(false);
      }
      setIsLoading2(false);
      dispatch(invoiceActions.setInvoiceDetails([]));
      dispatch(invoiceActions.setInvoicesTextractData([]));
      navigate(`/transactions/invoice/${nextInvoice?.stockist_invoice_attachment_id}/${0}`);
      setNetInvoiceAmount("");
    } catch (error: any) {
      console.log(error, "error");
      toast({ description: error?.response?.data?.message, variant: "destructive" });
      setIsLoading2(false);
    } finally {
      setConfirmingInvoice(false);
      isRequestInProgress.current = false;
    }
  }, 1000);

  const fetchProductsDataForItemCode = async (query: string) => {
    if (useOrderedProducts === "unknown") {
      return;
    }
    try {
      return await getSimilarCompanyProducts(invoiceDetails?.company_code, "", query, 1, 10);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchOrderedProductsByItemCode = async (query: string) => {
    if (useOrderedProducts === "unknown") {
      return;
    }
    try {
      return await getOrderedProducts({
        product_name: "",
        counter_code: invoiceDetails?.counter_code,
        item_code: query,
        page: 1,
        page_size: 10,
        company_stockist_code: invoiceDetails?.company_stockist_code,
        invoice_date: invoiceDetails?.invoice?.new_invoice_date,
        company_code: invoiceDetails?.company_code,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handlePercentageChange = (e?: any) => {
    const { setValue }: any = smartFormInstance;
    const percentage = Number(e) / 100;
    const discountAmount = percentage * quantity * price;
    setDiscountAmount(Number(discountAmount?.toFixed(2)));
    setDiscountPercentage(Number(e));
    setValue("discountAmount", discountAmount?.toFixed(2));
    setValue("discountPercentage", Number(e));
  };

  const handleAmountChange = (e?: any) => {
    const { setValue }: any = smartFormInstance;
    const amount = Number(e);
    const discountPercentage = (amount / (quantity * price)) * 100;
    setDiscountAmount(amount);
    setDiscountPercentage(Number(discountPercentage?.toFixed(2)));
    setValue("discountAmount", amount);
    setValue("discountPercentage", discountPercentage?.toFixed(2));
  };

  // const handleBatchNo = (e?: any) => {
  //   setBatchNo(e);
  // };

  const handleAutocompleteChange = async (data: any) => {
    const { setValue }: any = smartFormInstance;
    const value = data;
    const { isCustom } = value;
    if (value) {
      !isCustom && setValue("itemName", value?.product_name);
      !isCustom && setValue("itemCode", value?.item_code);
      // setValue("mrp", value?.mrp || "");
      // setValue("price", value?.ptr || "");
      setPrice(value?.ptr);
      setValue("batchNo", "");
      setValue("discountAmount", "");
      setValue("discountPercentage", "");
      setValue("freeQty", "");
      setValue("quantity", "");
      setValue("schemeType", "");
      setValue("gst", "");
      setValue("itemPrice", "");
      setValue("price", "");
      setValue("ptr", "");
      setDiscountPercentage(0);
      setDiscountAmount(0);

      setItemCode(value?.item_code);
    } else {
      // setValue("mrp", "");
      // setValue("price", "");
      setValue("schemeType", "");
      setValue("freeQty", "");
      setValue("discountPercentage", "");
      setValue("discountAmount", "");
      setValue("batchNo", "");
      setValue("itemCode", "");
    }
  };

  const resetFormFields = () => {
    const { reset }: any = smartFormInstance;

    // Reset form fields
    reset();

    // Clear additional state values
    setQuantity(0);
    setPriceList([]);
    setPrice(0);
    setBatchList([]);
    setDiscountPercentage(0);
    setDiscountAmount(0);
    setSelectedPriceIdentifier({
      key: "",
      description: "",
      priceldentifier_id: 0,
    });
    setFetchTrigger(fetchTrigger + 1);
    // setTimeout(() => {
    //   // Focus the Item Name input using the input's ID
    //   const itemNameInput = document.querySelector('[name="itemName"]') as HTMLInputElement;
    //   if (itemNameInput) {
    //     itemNameInput.focus();
    //   }
    // }, 0);
  };

  const handleRadioButtonsForForm = (value: string) => {
    setUseOrderedProducts(value);
    resetFormFields();
  };

  /**
   * Calculates the total invoice amount including GST and discounts
   * Formula: Sum of [(Gross Amount - Discount) + GST] for each item
   * @returns {number} Total invoice amount
   */
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const amountAfterCashDiscount = subtotal - (subtotal * cashDiscountPercent) / 100;
    const totalGst = calculateTotalGst();
    const tcsCalculatedAmount = amountAfterCashDiscount + totalGst + ((amountAfterCashDiscount + totalGst) * tcs) / 100;
    // return amountAfterCashDiscount + totalGst;
    return tcsCalculatedAmount;
  };

  /**
   * Calculates the raw subtotal before any discounts or taxes
   * Simply sums up the gross amounts of all items
   * @returns {number} Subtotal amount
   */
  const calculateInitialSubtotal = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const gross = Number(item.gross_amt);
      const discount = Number(item.discount_amt);

      // Skip items where gross_amt is not a number or is 0
      if (!gross || isNaN(gross)) return;

      total += gross - (isNaN(discount) ? 0 : discount);
    });
    return total;
  };

  /**
   * Calculates final subtotal after all discounts (including cash discount)
   */
  const calculateSubtotal = () => {
    const initialSubtotal = calculateInitialSubtotal();
    return initialSubtotal;
  };

  // const calculateGstAmount = (item: any) => {
  //   const { gross_amt, gst, discount_amt } = item;
  //   const amountAfterDiscount = Number(gross_amt) - Number(discount_amt);
  //   const gstAmount = (amountAfterDiscount * Number(gst)) / 100;
  //   return gstAmount;
  // };

  const calculateTotalGst = () => {
    let total = 0;
    mappedData?.forEach((item: any) => {
      const { gross_amt, gst, discount_amt, p_gst_perc } = item;

      // Skip items with 0 or falsy gross amount
      if (!gross_amt || Number(gross_amt) === 0) return;

      const amountAfterItemDiscount = Number(gross_amt) - Number(discount_amt);
      const amountAfterCashDiscount = amountAfterItemDiscount - (amountAfterItemDiscount * cashDiscountPercent) / 100;

      const gstAmount = (amountAfterCashDiscount * Number(gst || p_gst_perc)) / 100;
      total += gstAmount;
    });
    return total;
  };

  useEffect(() => {
    if (discountAmount || discountPercentage) {
      if (discountAmount) {
        handleAmountChange(discountAmount);
      } else {
        handlePercentageChange(discountPercentage);
      }
    }
  }, [quantity]);

  /**
   * Effect hook to fetch price and batch data when item code changes
   * Only runs for known/ordered products (not unknown items)
   * Makes parallel API calls for efficiency
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both price list and batch list simultaneously
        Promise.all([
          getInvoicePriceList(itemCode).then((res: any) => setPriceList(res?.data?.data)),
          getInvoiceBatchList(itemCode, company_code).then((res: any) => {
            const apiBatches = res?.data?.data || [];
            const combinedBatches = [...apiBatches, ...unknownBatch];
            setBatchList(combinedBatches);
          }),
        ]);
      } catch (error) {
        console.log(error);
      }
    };

    // Only fetch data if we have a valid item code and it's not an unknown product
    if (itemCode && useOrderedProducts !== "unknown") {
      fetchData();
    }
  }, [itemCode, useOrderedProducts, fetchTrigger]);

  /**
   * Effect hook to fetch initial invoice details
   * Runs once on component mount
   */

  useEffect(() => {
    if (invoicesTextractData?.length > 0) {
      const itemCodes: Set<string> = new Set();
      invoicesTextractData?.filter((item: any) => item?.gst == "0")?.map((item: any) => itemCodes.add(item?.item_code));
      const itemCodesString = Array.from(itemCodes).join(",");
      if (itemCodesString.length > 0) {
        getProductGstDetails(itemCodesString).then((response: any) => {
          const updatedData = invoicesTextractData.map((item: any) => {
            const gstDetails = response?.data?.find((gst: any) => gst?.item_code === item?.item_code);
            return {
              invoice_product_id: item?.invoice_product_id,
              unique_key: item?.invoice_product_id,
              invoice_id: invoiceDetails?.invoice?.invoice_id,
              item_description: item?.item_description,
              item_code: item?.item_code,
              quantity: item?.quantity,
              gst: item?.gst == "0" ? gstDetails?.gst_pc : item?.gst,
              cash_dis_perc: item?.cash_dis_perc ?? 0,
              ptr: Number(item?.ptr),
              discount_type: item?.discount_type,
              free_qty: item?.free_qty,
              batch_no: item?.batch_no,
              inv_rate: item?.inv_rate,
              inv_mrp: item?.inv_mrp,
              discount_amt: item?.discount_amt,
              counter_code: invoiceDetails?.counter_code,
              is_item_unknown: item?.is_item_unknown,
              gross_amt: (Number(item?.ptr) * Number(item?.quantity))?.toFixed(2),
              ucode: generateUniqueID() //M10-9682
            }
          });
          dispatch(invoiceActions.setInvoicesTextractData(updatedData));
          setMappedData(updatedData);
        })
      } else {
        //M10-9682
        const updatedData = invoicesTextractData.map((item: any) => ({
          ...item,
          ucode: generateUniqueID()
        }));
        dispatch(invoiceActions.setInvoicesTextractData(updatedData));
        setMappedData(updatedData);//M10-9682
      }

    }
    // if (invoiceDetails?.length == 0) {
    //   fetchInvoiceDetails();
    // }
  }, []);

  useEffect(() => {
    dispatch(invoiceActions.setInvoiceDetails([]));
    dispatch(invoiceActions.setInvoicesTextractData([]));
    resetFormFields();
    fetchInvoiceDetails();
  }, [stockist_invoice_attachment_id]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <InvoiceLayout>
      <Fragment>
        <Transition
          as={Fragment}
          show={isRemarksShown}
          enter="transition transition ease-in-out duration-200 transform"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition  ease-in-out duration-200 transform"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <div className="fixed right-0 inset-0 top-0 z-[500]">
            <InvoiceRemarks invoiceApprovalData={invoiceDetails} invoice_id={Number(invoiceDetails?.invoice?.invoice_id)} ref={ref} />
          </div>
        </Transition>
      </Fragment>
      <div className="flex justify-between px-[3.75rem] mt-6  pb-12 shadow-md border-b-[1px] border-b-[#E9E9E9]">
        <div className="flex items-center justify-between w-full">
          <div className="flex flex-col">
            <div
              className="flex items-center cursor-pointer font-semibold px-0 py-2 text-lg text-[#7F56D9]"
              onClick={() => navigate("/transactions/invoice/0")}
            >
              <img src={PurpleLeftArrowIcon} alt="back" className="w-3 h-3 mr-3" />
              Back to Invoices
            </div>
            <h1 className="text-[#49484C] font-bold text-[2rem]">Invoice Entry</h1>
          </div>
        </div>

        {viewRemarks && (
          <div className="absolute  mt-[1.16rem] mr-[1.16rem] top-60 right-0 cursor-pointer" onClick={() => setIsRemarksShown(true)}>
            <div className="relative">
              <MessageCircle className="text-gray-400 text-[24px]" />
            </div>
          </div>
        )}
      </div>

      <div className="px-[3.75rem] mx-auto mt-10 relative">
        <div
          onClick={() => {
            setVersioningModal((prev) => !prev);
          }}
          className="p-1 py-3 w-[5%] h-auto border-l-2 border-b-2 absolute top-12 right-0 -mt-12"
        >
          <img onClick={() => { }} src={Book} alt="React Logo" className="h-10 w-14" />
        </div>
        {VersioningModal && (
          <div ref={outSideClickRef}>
            <VersioningCrate type="Invoice" invoiceId={invoiceDetails?.invoice?.invoice_id} />
          </div>
        )}
        <ul className="mt-5">
          <li className="my-5 mb-10 rounded-lg">
            <InvoiceDetailCard
              isExpandedInitial={true}
              isExpandable={false}
              status={invoiceDetails?.fileStatusHdr?.fuid_status_desc}
              checkDataType={false}
              data={getInvoiceDetailsInfo(invoiceDetails)}
              ewayData={ewayData(invoiceDetails)}
              viewDetails={false}
              invoiceData={invoiceDetails}
              // handleAddItem={handleAddItem}
              fetchInvoiceDetails={fetchInvoiceDetails}
              isEwayExempt={isEwayExempt}
              tab={0}
            />
          </li>
        </ul>
        {invoiceDetails?.invoice && (
          <div className="p-0 mb-10 border-2 rounded-lg">
            <div className="m-5">
              <div className="flex items-center justify-between">
                <p className="mb-2 text-lg font-semibold">Invoice Details</p>
                <Button className="w-10 h-10 bg-[#F1F1F1] border border-1-black" onClick={handleToggle}>
                  <img className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
                </Button>
              </div>
              <div className="flex items-center gap-x-5">
                <div className="flex items-center mb-5 space-x-6">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="orderedProducts"
                      name="productType"
                      value="ordered"
                      checked={useOrderedProducts === "ordered"}
                      onChange={() => handleRadioButtonsForForm("ordered")}
                      className="mr-2"
                    />
                    <label htmlFor="orderedProducts" className="text-sm text-gray-500">
                      Ordered Products
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="companyProducts"
                      name="productType"
                      value="company"
                      checked={useOrderedProducts === "company"}
                      onChange={() => handleRadioButtonsForForm("company")}
                      className="mr-2"
                    />
                    <label htmlFor="companyProducts" className="text-sm text-gray-500">
                      Company Products
                    </label>
                  </div>
                </div>
                <div className="flex items-center mb-5">
                  <input
                    type="radio"
                    id="unknownOrder"
                    name="productType"
                    value="unknown"
                    checked={useOrderedProducts === "unknown"}
                    onChange={() => handleRadioButtonsForForm("unknown")}
                    className="mr-2"
                  />
                  <label htmlFor="unknownOrder" className="text-sm text-gray-500">
                    Unknown Order
                  </label>
                </div>
              </div>
              <div
                className={`transition-all duration-500 ease-in-out ${isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`}
                style={{ transitionProperty: "max-height, opacity" }}
              >
                <InvoiceEntryForm
                  handleFormSubmit={handleFormSubmit}
                  smartFormInstance={smartFormInstance}
                  useOrderedProducts={useOrderedProducts}
                  handleAutocompleteChange={handleAutocompleteChange}
                  selectedPriceIdentifier={selectedPriceIdentifier}
                  setSelectedBatch={setSelectedBatch}
                  schema={useOrderedProducts === "unknown" ? schema2 : schema}
                  fetchOrderedProductsByItemCode={fetchOrderedProductsByItemCode}
                  fetchProductsDataForItemCode={fetchProductsDataForItemCode}
                  batchList={batchList}
                  priceList={priceList}
                  gstAmbiguous={gstAmbiguous}
                  setGstAmbiguous={setGstAmbiguous}
                  setPrice={setPrice}
                  quantity={quantity}
                  setQuantity={setQuantity}
                  priceIdentifierList={priceIdentifierList}
                  setSelectedPriceIdentifier={setSelectedPriceIdentifier}
                  discountPercentage={discountPercentage}
                  discountAmount={discountAmount}
                  handlePercentageChange={handlePercentageChange}
                  handleAmountChange={handleAmountChange}
                />
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col mt-5 gap-x-5 [@media(min-width:1580px)]:flex-row">
          <div className="flex flex-col w-full gap-4 p-8 border rounded-lg [@media(min-width:1580px)]:w-9/12">
            <SubTable
              isLoading={isLoading2}
              title="Items Added"
              subtitle="Review and manage all items added"
              showheader={true}
              columns={columns}
              data={rowsForItemsAddedTable(false)}
              enableShowError={true}
            />
            <SubTable
              isLoading={isLoading2}
              title="Unknown Order"
              subtitle="Review and manage all items added"
              showheader={true}
              columns={columns2}
              data={rowsForItemsAddedTable(true)}
              enableShowError={true}
            />
            <Button
              className="w-fit h-9 mt-0 font-normal border border-[#D0D0D0]  bg-[#F1F1F1] shadow-sm"
              onClick={() => {
                navigate(`/transactions/invoice/product-mapping/${invoiceDetails?.stockist_invoice_attachment_id}`);
              }}
            >
              Map Products
            </Button>
          </div>

          <div className="w-4/12 p-8 mt-10 border rounded-lg [@media(min-width:1580px)]:w-3/12 [@media(min-width:1580px)]:mt-0">
            <p className="mb-2 text-lg font-semibold">Calculations</p>
            <div className="flex flex-col pb-10 gap-y-1">
              <p className="flex justify-between py-2 text-sm">
                Subtotal <span className="">₹{(calculateSubtotal() - cashDiscountAmount)?.toFixed(2)}</span>
              </p>
              <p className="flex justify-between py-2 text-sm">
                <span className="mr-2">Cash Discount %</span>
                <div className="">
                  <Input
                    enableArrows={false}
                    showIcon={false}
                    type="number"
                    placeholder="0"
                    value={cashDiscountPercent}
                    onChange={(e: any) => handleCashDiscountPercentChange(e?.target?.value)}
                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                  />{" "}
                </div>
              </p>
              <p className="flex justify-between py-2 text-sm">
                <span className="mr-2">Cash Discount Amount</span>
                <div className="">
                  <Input
                    enableArrows={false}
                    showIcon={false}
                    type="number"
                    placeholder="0"
                    value={cashDiscountAmount}
                    onChange={(e: any) => handleCashDiscountAmountChange(e?.target?.value)}
                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                  />{" "}
                </div>
              </p>
              <p className="flex justify-between py-2 text-sm">
                <span className="mr-2">TCS %</span>
                <div className="">
                  <Input
                    enableArrows={false}
                    showIcon={false}
                    type="number"
                    placeholder="TCS"
                    value={tcs}
                    onChange={(e: any) => setTcs(e?.target?.value)}
                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                  />{" "}
                </div>
              </p>
              <p className="flex justify-between py-2 text-sm">
                GST Amount<span className="font-semibold">₹{calculateTotalGst()?.toFixed(2)}</span>
              </p>
              <p className="flex justify-between py-2 text-sm">
                Total (as calculated) <span className="font-semibold">₹{calculateTotal()?.toFixed(2)}</span>
              </p>
              <p className="flex justify-between py-2 text-sm">
                <span className="mr-2">
                  Net Inv. Amt.<span className="text-red-500"> *</span>
                </span>
                <div className="">
                  <Input
                    enableArrows={false}
                    showIcon={false}
                    type="number"
                    placeholder="Net Amt."
                    value={netInvoiceAmount}
                    onChange={(e: any) => setNetInvoiceAmount(e?.target?.value)}
                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                  />{" "}
                </div>
              </p>
              <Button
                disabled={isRequestInProgress.current}
                className="w-full h-9 mt-5 font-normal text-white bg-[#8051FF] shadow-sm"
                isLoading={isConfirmingInvoice}
                loaderSize="small"
                onClick={handleSubmitInvoice}
                module={fineGrainedPermissionsKeys.INVOICE.CONFIRMINVOICE.module}
                action={fineGrainedPermissionsKeys.INVOICE.CONFIRMINVOICE.action}
              >
                Confirm Invoice
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Modal isOpen={isEwayApplicable} isHeader={false} onCloseModal={() => setIsEwayApplicable(false)} title="Eway Bill">
        <EwayDetails isUpadinng={false} onSuccess={fetchInvoiceDetails} onCloseModal={() => {
          setIsEwayApplicable(false)
        }} invoiceData={invoiceDetails} />
      </Modal>
    </InvoiceLayout>
  );
};
